import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HistoricalDataModalData, HistoricalDataPreloadedPayload } from '../../models/historical-data.interface';
import { GridColumn } from '../../models/data-table.interface';
import { HistoricalDataService } from '../../services/historical-data/historical-data.service';
import { convertRtfToHtml, mergeVitalTableData, processVitalSignsResponse } from '../../util/utils';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { MedicalRequestEntry, SoapNote, sectionName } from '../../models/dictation.interface';
import { MANUAL_REFRESH_IDENTIFIER } from '../../util/constants';
import { ManualResponse } from '../../models/manual-refresh.interface';
import { VitalSigns } from '../../models/vital-signs.interface';
import { AllergyResponse } from '../../models/allergy.interface';
import { PageEvent } from '@angular/material/paginator';
import { Immunization } from '../../models/immunization.interface';
import { MedicationRequest } from '../../models/medical-request.interfcae';
import { Labhistory } from '../../models/labhistory';
import { ConditionResponse } from '../../models/condition.interface';
import DOMPurify from 'dompurify';
import { getConsultationDisplayText } from '../../util/consultation-display.utils';

type ConsultationPreviewRecord = SoapNote & {
  subjectivePlain?: string;
  objectivePlain?: string;
  assessmentPlain?: string;
  planPlain?: string;
};

@Component({
  standalone: false,
  selector: 'cva-historical-data',
  templateUrl: './historical-data.component.html',
  styleUrl: './historical-data.component.scss',

})
export class HistoricalDataComponent implements OnInit {
  showPreviousconsultation: boolean = false;
  showMedicationRequestData: boolean = false;
  showLabHistory: boolean = false;

  historicalData: HistoricalDataModalData;

  labdata: Labhistory[] = [];

  labDataArray: { date: string; entries: { displayName: string; resultData: HTMLElement[][]; }[]; }[] | undefined | null;

  columns: GridColumn<Record<string, string>>[] = [];

  tableData: Record<string, string>[] = [];

  loading: boolean = true;

  stickyColumnPosition: number = -1;

  showPagination: boolean = false;
  pageSize: number = 5;
  consultationHistory: SoapNote[] = [];
  consultationHistoryCurPage: SoapNote[] = [];
  consultationHistoryPageSize = 5;
  consultationHistoryPageIndex = 0;

  medicalRequestData: MedicalRequestEntry[] = [];

  medicalRequestCurPage: { date: string, table: { medication: string, duration: string, qty: string, route: string, instruction: string }[] }[] = [];

  medicationRequestPageIndex = 0;
  medicationRequestPageSize = 5;
  labResults: { date: string; entries: { displayName: string; resultData: HTMLElement[][]; }[]; }[] = [];
  labResultsPage: { date: string; entries: { displayName: string; resultData: HTMLElement[][]; }[]; }[] = [];
  labResultsPageIndex = 0;
  labResultsPageSize = 5;
  selectedLabEntryByDate: Record<string, number> = {};
  labTestSearchTerm = '';

  manualRefreshIdentifier!: MANUAL_REFRESH_IDENTIFIER;

  hasMoreData = true;
  showViewMoreBtn = false;
  expandedConsultationNoteKeys = new Set<string>();

  get dialogHeaderClass(): string {
    switch (this.historicalData?.category) {
      case 'Allergy': return 'dialog-header-allergies';
      case 'Observations': return 'dialog-header-vitals';
      case 'Consultations': return 'dialog-header-consultation';
      case 'Medications': return 'dialog-header-medications';
      case 'immunization': return 'dialog-header-immunizations';
      case 'Lab Results': return 'dialog-header-lab';
      case 'Conditions': return 'dialog-header-conditions';
      default: return '';
    }
  }

  get dialogHeaderIcon(): string {
    switch (this.historicalData?.category) {
      case 'Allergy': return 'warning_amber';
      case 'Observations': return 'monitor_heart';
      case 'Consultations': return 'description';
      case 'Medications': return 'medication';
      case 'immunization': return 'vaccines';
      case 'Lab Results': return 'biotech';
      case 'Conditions': return 'assignment_turned_in';
      default: return '';
    }
  }

  get dialogContentClass(): string {
    switch (this.historicalData?.category) {
      case 'Allergy': return 'content-allergies';
      case 'Observations': return 'content-vitals';
      case 'Consultations': return 'content-consultation';
      case 'Medications': return 'content-medications';
      case 'immunization': return 'content-immunizations';
      case 'Lab Results': return 'content-lab';
      case 'Conditions': return 'content-conditions';
      default: return '';
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly modalData: HistoricalDataModalData,
    private readonly historicalDataService: HistoricalDataService,
    private readonly datePipe: DatePipe,
    private readonly sanitizer: DomSanitizer,
  ) {
    this.historicalData = modalData;
    this.setManualRefreshIdentifier();
  }

  ngOnInit(): void {
    this.reset();
    if (this.applyPreloadedData(this.historicalData.preloadedData)) {
      return;
    }

    // modify the variables columns and tableData here
    switch (this.historicalData.category) {
      case 'Consultations':
        this.getMedicalSummaryData();
        break;
      case 'Observations':
        this.getVitalSignsData();
        break;
      case 'Allergy':
        this.getAllergyData();
        break;
      case 'Prescription/Medication':
        this.getMedicationData();
        break
      case 'immunization':
        this.getImmunization();
        break;
      case 'Medications':
        this.getMedicationRequestData();
        break;
      case 'Lab Results':
        this.getLabResultsData();
        break;
      case 'Conditions':
        this.getConditionData();
        break;
      default:
        this.loading = false;
    }
  }

  private applyPreloadedData(preloaded?: HistoricalDataPreloadedPayload): boolean {
    if (!preloaded) {
      return false;
    }

    switch (this.historicalData.category) {
      case 'Consultations': {
        if (!preloaded.consultations) {
          return false;
        }
        this.showPreviousconsultation = true;
        this.consultationHistory = [...preloaded.consultations].sort((a, b) => {
          const aDate = new Date(a.soapNoteDate || 0).getTime();
          const bDate = new Date(b.soapNoteDate || 0).getTime();
          return bDate - aDate;
        });
        this.hasMoreData = this.consultationHistory.length !== 0;
        this.setConsultationHistoryPageEntries();
        this.updateViewMoreVisibility<SoapNote>(
          this.consultationHistory,
          this.consultationHistoryPageSize,
          this.consultationHistoryPageIndex
        );
        this.loading = false;
        return true;
      }
      case 'Observations': {
        return this.applyPreloadedVitals(preloaded.vitals);
      }
      case 'Allergy': {
        if (!preloaded.allergies) {
          return false;
        }
        this.setAllergyRecords(preloaded.allergies);
        this.setAllergyColumns();
        this.loading = false;
        return true;
      }
      case 'immunization': {
        if (!preloaded.immunizations) {
          return false;
        }
        const immunizations = [...preloaded.immunizations].sort(
          (a, b) => new Date(b.occurrenceDateTime).getTime() - new Date(a.occurrenceDateTime).getTime()
        );
        this.setImmunization(immunizations);
        this.setImmunizationColumns();
        this.loading = false;
        return true;
      }
      case 'Medications': {
        if (!preloaded.medicationRequests) {
          return false;
        }
        this.showMedicationRequestData = true;
        this.setMedicationRequestColumns();
        this.hasMoreData = preloaded.medicationRequests.length !== 0;
        this.setMedicationRecords(preloaded.medicationRequests);
        this.updateViewMoreVisibility<MedicalRequestEntry>(
          this.medicalRequestData,
          this.medicationRequestPageSize,
          this.medicationRequestPageIndex
        );
        this.loading = false;
        return true;
      }
      case 'Lab Results': {
        if (!preloaded.labResults) {
          return false;
        }
        this.showLabHistory = true;
        void this.applyPreloadedLabResults(preloaded.labResults);
        return true;
      }
      case 'Conditions': {
        if (!preloaded.conditions) {
          return false;
        }
        this.setConditionRecords(preloaded.conditions);
        this.setConditionColumns();
        this.loading = false;
        return true;
      }
      default:
        return false;
    }
  }

  private applyPreloadedVitals(vitals?: VitalSigns[]): boolean {
    if (!vitals) {
      return false;
    }

    this.stickyColumnPosition = 0;
    this.showViewMoreBtn = vitals.length !== 0;
    this.setVitalRecords(vitals);
    this.loading = false;
    return true;
  }

  private async applyPreloadedLabResults(labResults: Labhistory[]): Promise<void> {
    this.loading = true;
    await this.setLabResults(labResults);
    this.setLabResultsPageEntries();
    this.loading = false;
  }

  private setAllergyColumns(): void {
    this.columns = [
      {
        field: 'allergen',
        label: 'Allergen',
      },
      {
        field: 'type',
        label: 'Type',
      },
      {
        field: 'category',
        label: 'Category',
      },
      {
        field: 'criticality',
        label: 'Criticality',
      },

      {
        field: 'clinicalStatus',
        label: 'Clinical Status',
      },
      {
        field: 'verificationStatus',
        label: 'Verification Status',
      },
      {
        field: 'onsetDate',
        label: 'Onset Date',
      },
      {
        field: 'recordedDate',
        label: 'Last Occurred On',
      },
    ];
  }

  private setMedicationRequestColumns(): void {
    this.columns = [
      {
        field: 'medication',
        label: 'Medication'
      },
      {
        field: 'duration',
        label: 'Duration',
      },
      {
        field: 'qty',
        label: 'Quantity',
      },
      {
        field: 'route',
        label: 'Route',
      },
      {
        field: 'instruction',
        label: 'Instruction',
      },
    ];
  }

  private setImmunizationColumns(): void {
    this.columns = [
      {
        field: 'displayName',
        label: 'Vaccine Name',
        tdClass: 'normal-text'
      },
      {
        field: 'occurrenceDate',
        label: 'Occurrence Date',
        tdClass: 'normal-text'
      },
      {
        field: 'expirationDate',
        label: 'Expiration Date',
        tdClass: 'normal-text'
      },
      {
        field: 'status',
        label: 'Status ',
        tdClass: 'normal-text'
      },
      { field: 'note', label: 'Note', width: '15%', tdClass: 'notes-field normal-text text-break', hasLargeText: true },
      {
        field: 'practitionerName',
        label: 'Performer',
        tdClass: 'normal-text'
      }

    ];
  }

  private setConditionColumns(): void {
    this.columns = [
      {
        field: 'condition',
        label: 'Condition',
        tdClass: 'normal-text'
      },
      {
        field: 'status',
        label: 'Status',
        tdClass: 'normal-text'
      },
      {
        field: 'onsetDateTime',
        label: 'Onset Date',
        tdClass: 'normal-text'
      },
      {
        field: 'recordedDate',
        label: 'Recorded Date',
        tdClass: 'normal-text'
      }
    ];
  }

  getSafeHTML(htmlElements: HTMLElement[]) {
    if (htmlElements) {
      const div = document.createElement('div');
      div.append(...htmlElements);
      //sanitizing to remove unsafe elements
      const sanitizedDOM = DOMPurify.sanitize(div.innerHTML);
      return this.sanitizer.bypassSecurityTrustHtml(sanitizedDOM);  //NOSONAR
    }
    return '';
  }

  public getMedicalSummaryData() {
    const { startDate, endDate } = this.historicalDataService.getNextRange();
    this.showPreviousconsultation = true;
    this.loading = true;
    this.historicalDataService
      .getPreviousConsultationByPatientId$(this.historicalData.patientId, startDate, endDate)
      .subscribe({
        next: (historical: SoapNote[]) => {
          this.hasMoreData = historical.length !== 0;
          this.setSoapNoteRecords(historical).then(() => {
            this.updateViewMoreVisibility<SoapNote>(
              this.consultationHistory,
              this.consultationHistoryPageSize,
              this.consultationHistoryPageIndex
            );
          })
        },
        error: () => (this.loading = false)
      });
  }

  private getAllergyData(): void {
    this.loading = true;
    this.historicalDataService
      .getAllergiesByPatientId$(this.historicalData.patientId)
      .subscribe({
        next: (allergies) => {
          this.setAllergyRecords(allergies);
          this.setAllergyColumns();

          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  getVitalSignsData() {
    const { startDate, endDate } = this.historicalDataService.getNextRange();
    this.stickyColumnPosition = 0;
    this.loading = true;
    this.historicalDataService
      .getVitalSignsForPatient$(this.historicalData.patientId, startDate, endDate)
      .subscribe({
        next: (vitalSigns: VitalSigns[]) => {
          this.showViewMoreBtn = vitalSigns.length !== 0;
          this.setVitalRecords(vitalSigns);
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  private getMedicationData() {
    this.loading = false;
  }

  getMedicationRequestData() {
    this.loading = true;
    this.showMedicationRequestData = true;
    this.setMedicationRequestColumns();
    this.historicalDataService.resetDateRange();
    this.fetchMedicationRequestPages([]);
  }

  private fetchMedicationRequestPages(accumulatedData: MedicationRequest[]) {
    const { startDate, endDate } = this.historicalDataService.getNextRange();
    this.historicalDataService
      .getMedicationsForPatient$(this.historicalData.patientId, startDate, endDate)
      .subscribe({
        next: (medicaldata: MedicationRequest[]) => {
          if (medicaldata.length === 0) {
            this.hasMoreData = false;
            this.showViewMoreBtn = false;
            this.medicalRequestData = this.mapMedicalRequestData(accumulatedData);
            this.setMedicalPageEntries();
            this.setPagination(this.medicalRequestData);
            this.loading = false;
            return;
          }

          this.fetchMedicationRequestPages([...accumulatedData, ...medicaldata]);
        },
        error: () => {
          this.hasMoreData = false;
          this.showViewMoreBtn = false;
          this.medicalRequestData = this.mapMedicalRequestData(accumulatedData);
          this.setMedicalPageEntries();
          this.setPagination(this.medicalRequestData);
          this.loading = false;
        },
      });
  }
  setMedicationRecords(medicalData: MedicationRequest[]) {
    this.medicalRequestData.push(...this.mapMedicalRequestData(medicalData));

    this.setMedicalPageEntries();
    this.setPagination(this.medicalRequestData);

  }

  public mapMedicalRequestData(medicalData: MedicationRequest[]) {
    const map = new Map<string, {
      medication: string,
      duration: string,
      qty: string,
      route: string,
      instruction: string
    }[]>();
    medicalData.forEach((item) => {
      const date = new Date(item.authoredOn).toISOString().split('T')[0]; // Get the date part only
      const table = item.dosageInstructions.map((instruction) => ({
        medication: item.medicationText,
        duration: item.dispenseRequest?.expectedSupplyDuration
          ? `${item.dispenseRequest.expectedSupplyDuration?.value} ${item.dispenseRequest.expectedSupplyDuration.unit}`
          : "-",
        qty: item.dispenseRequest.quantity.value.toString(),
        route: instruction.routeText,
        instruction: instruction.timingText ?? "-"
      }));
      if (map.has(date)) {
        map.get(date)?.push(...table);
      } else {
        map.set(date, table);
      }
    });

    // Convert the map to an array for further processing or rendering
    const medicalRequestData = Array.from(map.entries()).map(([date, table]) => ({
      date,
      table
    }));

    medicalRequestData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return medicalRequestData;
  }

  async setSoapNoteRecords(historical: SoapNote[]) {
    try {

      this.loading = false;
      const content = historical.map(async (history: SoapNote) => {
        const historyWithPreview = history as ConsultationPreviewRecord;
        for (const property in history) {
          if (
            sectionName.assessment == property ||
            sectionName.subjective == property ||
            sectionName.objective == property ||
            sectionName.plan == property
          ) {
            if (history[property]) {
              const htmlElements = await convertRtfToHtml(history[property] as string);
              history[property] = this.getSafeHTML(htmlElements);
              const plainText = this.getPlainTextFromHtmlElements(htmlElements);
              switch (property) {
                case sectionName.subjective:
                  historyWithPreview.subjectivePlain = plainText;
                  break;
                case sectionName.objective:
                  historyWithPreview.objectivePlain = plainText;
                  break;
                case sectionName.assessment:
                  historyWithPreview.assessmentPlain = plainText;
                  break;
                case sectionName.plan:
                  historyWithPreview.planPlain = plainText;
                  break;
                default:
                  break;
              }

            }
          }
        }
        return history;
      });
      const consultationHistory = await Promise.all(content);
      consultationHistory.sort((a, b) => {
        const aDate = new Date(a.soapNoteDate);
        const bDate = new Date(b.soapNoteDate);
        if (aDate < bDate) {
          return 1;
        } else if (aDate > bDate) {
          return -1;
        }
        return 0;
      });
      this.consultationHistory.push(...consultationHistory);
      this.setConsultationHistoryPageEntries();
      this.setPagination(this.consultationHistory)
    } catch (error) {
      console.error(error);
    }
  }

  setVitalRecords(vitalSigns: VitalSigns[]) {
    this.showPagination = false;
    const data = processVitalSignsResponse(
      vitalSigns,
      (sortedDates: string[]) => {
        if (this.columns.length === 0) {
          this.columns.push({
            field: 'displayName',
            label: 'Parameters',
            width: "20px"
          });
        }

        const existingFields = new Set(this.columns.map(col => col.field));

        const uniqueDates = Array.from(new Set(sortedDates));

        const newColumns = uniqueDates
          .filter(date => !existingFields.has(date))
          .map(date => ({
            field: date,
            label: this.datePipe.transform(date, 'd MMM, yyyy') ?? '',
            width: "20px",
            isVital: true
          }));

        // Add new columns to the existing ones
        this.columns = [
          ...this.columns,
          ...newColumns,
        ];
      }
    );
    this.tableData = mergeVitalTableData(this.tableData, data)
  }

  setAllergyRecords(allergies: AllergyResponse[]) {
    this.setPagination(allergies)
    this.tableData = allergies.map((allergy) => {
      return {
        onsetDate: allergy.onsetDate
          ? (this.datePipe.transform(allergy.onsetDate, 'd MMM, y') as string)
          : '-',
        clinicalStatus:
          allergy.clinicalStatus?.code
            ? allergy.clinicalStatus.code
            : '-',
        verificationStatus:
          allergy.verificationStatus?.code
            ? allergy.verificationStatus.code
            : '-',
        criticality: allergy.criticality ? allergy.criticality : '-',
        category: allergy.category ? allergy.category : '-',
        type: allergy.type?.code ? allergy.type.code : '-',
        allergen:
          allergy.allergen?.display
            ? allergy.allergen.display
            : '-',
        recordedDate: allergy.recordedDate
          ? (this.datePipe.transform(
            allergy.recordedDate,
            'd MMM, y'
          ) as string)
          : '-',
      };
    });
  }

  setImmunization(immunizations: Immunization[]) {
    this.setPagination(immunizations)
    this.tableData = immunizations.map((immunization) => {
      return {
        "uid": immunization.uid,
        "expirationDate": immunization.expirationDate ? this.datePipe.transform(immunization.expirationDate, 'd MMM, y') as string : '-',
        "note": immunization.note ?? '-',
        "status": immunization.status,
        "displayName": immunization.vaccineName ?? '-',
        "patientId": immunization.patientId,
        "patientName": immunization.patientName,
        "occurrenceDate": immunization.occurrenceDateTime ? this.datePipe.transform(immunization.occurrenceDateTime, 'd MMM, y') as string : '-',
        "practitionerId": immunization.practitionerId,
        "practitionerName": immunization.practitionerName
      }
    })
  }

  setConditionRecords(conditions: ConditionResponse[]) {
    const sortedConditions = [...conditions].sort((a, b) =>
      this.getConditionTimestamp(b) - this.getConditionTimestamp(a)
    );
    this.setPagination(sortedConditions);
    this.tableData = sortedConditions.map((condition) => {
      return {
        condition: condition.condition ? condition.condition : '-',
        status: condition.status ? condition.status : '-',
        onsetDateTime: condition.onsetDateTime
          ? (this.datePipe.transform(condition.onsetDateTime, 'd MMM, y') as string)
          : '-',
        recordedDate: condition.recordedDate
          ? (this.datePipe.transform(condition.recordedDate, 'd MMM, y') as string)
          : '-',
      };
    });
  }

  private getConditionTimestamp(condition: ConditionResponse): number {
    const timestamp = new Date(
      condition.recordedDate || condition.onsetDateTime || 0
    ).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  setManualRefreshIdentifier() {
    switch (this.historicalData.category) {
      case 'Allergy':
        this.manualRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.ALLERGY;
        break;
      case 'Consultations':
        this.manualRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.SOAP_NOTE;
        break;
      case 'Observations':
        this.manualRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.OBSERVATIONS;
        break;
      case 'Lab Results':
        this.manualRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.LAB_RESULTS;
        break;
      case 'immunization':
        this.manualRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.IMMUNIZATION;
        break;
      case 'Medications':
        this.manualRefreshIdentifier = MANUAL_REFRESH_IDENTIFIER.MEDICATION_REQUEST;
        break;
      default:
        break;
    }
  }

  async setMedicalRequestData(data: MedicationRequest[]) {
    this.medicalRequestData = this.mapMedicalRequestData(data);
  }

  async setLabResults(data: Labhistory[]): Promise<void> {
    this.labdata = data;
    this.setPagination(data)
    try {
      this.labDataArray = await this.getMappedDataWithHtml();
    } catch (error) {
      console.error('Error occurred while processing lab results:', error);
    }
  }


  async patchRefreshedData(data: ManualResponse<unknown>) {
    switch (data.identifier) {
      case MANUAL_REFRESH_IDENTIFIER.ALLERGY:
        this.setAllergyRecords(data.records as AllergyResponse[]);
        break;
      case MANUAL_REFRESH_IDENTIFIER.SOAP_NOTE:
        this.setSoapNoteRecords(data.records as SoapNote[]);
        break;
      case MANUAL_REFRESH_IDENTIFIER.OBSERVATIONS:
        this.setVitalRecords(data.records as VitalSigns[]);
        break;
      case MANUAL_REFRESH_IDENTIFIER.IMMUNIZATION:
        this.setImmunization(data.records as Immunization[]);
        break;
      case MANUAL_REFRESH_IDENTIFIER.MEDICATION_REQUEST:
        this.setMedicalRequestData(data.records as MedicationRequest[]);
        break;
      case MANUAL_REFRESH_IDENTIFIER.LAB_RESULTS:
        this.setLabResults(data.records as Labhistory[]);
        break;
      default:
        break;
    }
  }

  handleConsultationHistoryPageEvent(pageEvent: PageEvent) {
    this.consultationHistoryPageIndex = pageEvent.pageIndex;
    this.consultationHistoryPageSize = pageEvent.pageSize;
    this.setConsultationHistoryPageEntries();
    this.updateViewMoreVisibility<SoapNote>(
      this.consultationHistory,
      this.consultationHistoryPageSize,
      this.consultationHistoryPageIndex
    );
  }

  getConsultationPreviewText(history: SoapNote): string {
    return getConsultationDisplayText(history);
  }

  hasFormattedConsultationNote(history: SoapNote): boolean {
    return !!(history.subjective || history.objective || history.assessment || history.plan);
  }

  hasConsultationPreviewText(history: SoapNote): boolean {
    return !!this.getConsultationPreviewText(history);
  }

  isConsultationNoteExpanded(history: SoapNote): boolean {
    return this.expandedConsultationNoteKeys.has(this.getConsultationHistoryKey(history));
  }

  toggleConsultationNoteExpanded(history: SoapNote): void {
    const noteKey = this.getConsultationHistoryKey(history);
    if (this.expandedConsultationNoteKeys.has(noteKey)) {
      this.expandedConsultationNoteKeys.delete(noteKey);
      return;
    }
    this.expandedConsultationNoteKeys.add(noteKey);
  }

  private getConsultationHistoryKey(history: SoapNote): string {
    return history.uid ?? history.tempUid ?? `${history.soapNoteDate}-${history.appointmentId}`;
  }

  private getPlainTextFromHtmlElements(htmlElements: HTMLElement[]): string {
    const div = document.createElement('div');
    const clonedElements = htmlElements.map((element) => element.cloneNode(true));
    div.append(...clonedElements);
    return this.normalizeWhitespace(div.textContent ?? '');
  }

  private getPlainTextFromUnknown(content: string | object): string {
    if (!content) {
      return '';
    }

    if (typeof content === 'string') {
      return this.getPlainTextFromMarkup(content);
    }

    const trustedContent = (content as { changingThisBreaksApplicationSecurity?: string }).changingThisBreaksApplicationSecurity;
    if (typeof trustedContent === 'string') {
      return this.getPlainTextFromMarkup(trustedContent);
    }

    return '';
  }

  private getPlainTextFromMarkup(markup: string): string {
    const div = document.createElement('div');
    div.innerHTML = markup;
    return this.normalizeWhitespace(div.textContent ?? '');
  }

  private formatConsultationDisplayText(value: string): string {
    if (!value) {
      return '';
    }

    const sectionPattern = /(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN|VITAL SIGNS)\s*:\s*/gi;
    const vitalLabelPattern = /(Alcohol Cessation(?:\s*\([^)]+\))?|Blood Pressure(?:\s*\([^)]+\))?|Body Mass Index(?:\s*\([^)]+\))?|Heart Rate(?:\s*\([^)]+\))?|Height(?:\s*\([^)]+\))?|Oxygen(?:\s*\([^)]+\))?|Respiratory Rate(?:\s*\([^)]+\))?|Smoking Cessation(?:\s*\([^)]+\))?|Temperature(?:\s*\([^)]+\))?|Weight(?:\s*\([^)]+\))?)\s*:/gi;

    return value
      // Ensure each SOAP section starts on a new line.
      .replace(sectionPattern, '\n$1:\n')
      // Break the vitals block into list-like lines for the expanded consultation view.
      .replace(vitalLabelPattern, '\n- $1:')
      // Treat " - " tokens from raw response as line separators.
      .replace(/\s-\s+/g, '\n- ')
      .replace(/\n-\s*/g, '\n- ')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private normalizeWhitespace(value: string): string {
    const normalizedLineEndings = value
      .replaceAll('\r\n', '\n')
      .replaceAll('\r', '\n');

    let result = '';
    let pendingSpace = false;

    for (const char of normalizedLineEndings) {
      if (char === ' ' || char === '\t') {
        pendingSpace = true;
        continue;
      }

      if (char === '\n') {
        // Drop leading/trailing spaces around newlines.
        pendingSpace = false;
        result += '\n';
        continue;
      }

      if (pendingSpace && result.length > 0 && result[result.length - 1] !== '\n') {
        result += ' ';
      }

      pendingSpace = false;
      result += char;
    }

    return result
      // Keep empty lines as separators, but collapse multiple blank lines to one.
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  updateViewMoreVisibility<T>(
    data: T[],
    pageSize: number,
    pageIndex: number,
  ) {
    if (pageSize > 0) {
      const totalPages = Math.ceil(data.length / pageSize);
      const isLastPage = pageIndex === totalPages - 1;
      this.showViewMoreBtn = isLastPage && this.hasMoreData;
    }
  }

  setConsultationHistoryPageEntries() {
    const startIndex =
      this.consultationHistoryPageIndex * this.consultationHistoryPageSize;
    const endIndex = startIndex + this.consultationHistoryPageSize;
    this.consultationHistoryCurPage = this.consultationHistory.slice(
      startIndex,
      endIndex
    );
  }

  handleLabResultsPageEvent(pageEvent: PageEvent) {
    this.labResultsPageIndex = pageEvent.pageIndex;
    this.labResultsPageSize = pageEvent.pageSize;
    this.setLabResultsPageEntries();
  }

  setLabResultsPageEntries() {
    const startIndex =
      this.labResultsPageIndex * this.labResultsPageSize;
    const endIndex = startIndex + this.labResultsPageSize;
    this.setPagination(this.labDataArray)
    if (this.labDataArray) {
      this.labResultsPage = this.labDataArray.slice(
        startIndex,
        endIndex
      );
      this.initializeLabEntrySelectionForPage();
    }
  }

  private initializeLabEntrySelectionForPage(): void {
    for (const item of this.labResultsPage) {
      const selectedIndex = this.selectedLabEntryByDate[item.date];
      if (selectedIndex == null || selectedIndex < 0 || selectedIndex >= item.entries.length) {
        this.selectedLabEntryByDate[item.date] = 0;
      }
    }
  }

  selectLabEntry(date: string, index: number): void {
    this.selectedLabEntryByDate[date] = index;
  }

  getSelectedLabEntry(item: { date: string; entries: { displayName: string; resultData: HTMLElement[][]; }[] }): { displayName: string; resultData: HTMLElement[][]; } | null {
    if (!item.entries.length) {
      return null;
    }

    const selectedIndex = this.selectedLabEntryByDate[item.date] ?? 0;
    return item.entries[selectedIndex] ?? item.entries[0];
  }

  getLabSearchMatches(): { testName: string; dates: string[] }[] {
    const searchTerm = this.labTestSearchTerm.trim().toLowerCase();
    if (!searchTerm || !this.labDataArray?.length) {
      return [];
    }

    const testDateMap = new Map<string, Set<string>>();

    for (const dateEntry of this.labDataArray) {
      for (const entry of dateEntry.entries) {
        const testName = (entry.displayName || '').trim();
        if (!testName || !testName.toLowerCase().includes(searchTerm)) {
          continue;
        }

        if (!testDateMap.has(testName)) {
          testDateMap.set(testName, new Set<string>());
        }

        testDateMap.get(testName)?.add(dateEntry.date);
      }
    }

    return Array.from(testDateMap.entries())
      .map(([testName, dateSet]) => ({
        testName,
        dates: Array.from(dateSet),
      }))
      .sort((a, b) => a.testName.localeCompare(b.testName));
  }

  hasActiveLabSearch(): boolean {
    return !!this.labTestSearchTerm.trim();
  }

  getFilteredLabEntries(): { date: string; displayName: string; resultData: HTMLElement[][] }[] {
    const searchTerm = this.labTestSearchTerm.trim().toLowerCase();
    if (!searchTerm || !this.labDataArray?.length) {
      return [];
    }

    const filteredEntries: { date: string; displayName: string; resultData: HTMLElement[][] }[] = [];

    for (const item of this.labDataArray) {
      for (const entry of item.entries) {
        if ((entry.displayName || '').toLowerCase().includes(searchTerm)) {
          filteredEntries.push({
            date: item.date,
            displayName: entry.displayName,
            resultData: entry.resultData,
          });
        }
      }
    }

    return filteredEntries;
  }

  getDisplayedLabResults(): { date: string; entries: { displayName: string; resultData: HTMLElement[][] }[] }[] {
    const searchTerm = this.labTestSearchTerm.trim().toLowerCase();

    if (!searchTerm) {
      return this.labResultsPage;
    }

    if (!this.labDataArray?.length) {
      return [];
    }

    return this.labDataArray
      .map((item) => ({
        date: item.date,
        entries: item.entries.filter((entry) =>
          (entry.displayName || '').toLowerCase().includes(searchTerm)
        ),
      }))
      .filter((item) => item.entries.length > 0);
  }

  getLabResultsData() {
    this.loading = true;
    this.showLabHistory = true;
    this.historicalDataService
      .getLabResults$(this.historicalData.patientId)
      .subscribe({
        next: (val) => {
          (async () => {
            if (val) {
              this.labdata = val;
              this.labDataArray = await this.getMappedDataWithHtml();
              this.setLabResultsPageEntries();
            }
            this.loading = false;
          })();
        },
        error: (err) => {
          console.log(err);
          this.loading = false;
        }
      })
  }

  public mapDataByDate() {
    const map = new Map<string, Map<string, string[]>>();

    for (const item of this.labdata) {
      const date = new Date(item.issuedOn);
      const formattedDate = this.datePipe.transform(date, 'dd/MM/yyyy') ?? '';

      if (!map.has(formattedDate)) {
        map.set(formattedDate, new Map<string, string[]>());
      }

      const dateMap = map.get(formattedDate)!;

      if (!dateMap.has(item.displayName)) {
        dateMap.set(item.displayName, []);
      }

      dateMap.get(item.displayName)!.push(item.resultData);
    }

    const formattedResult = new Map<string, Array<{ displayName: string, resultData: string[] }>>();

    for (const [date, dateMap] of map.entries()) {
      const dateEntries: Array<{ displayName: string, resultData: string[] }> = [];
      for (const [displayName, resultData] of dateMap.entries()) {
        dateEntries.push({ displayName, resultData });
      }
      formattedResult.set(date, dateEntries);
    }

    return formattedResult;
  }

  async getMappedDataWithHtml() {
    const dataMap = this.mapDataByDate();
    const resultArray: { date: string; entries: { displayName: string; resultData: string[] }[] }[] = [];

    for (const [date, dateMap] of dataMap.entries()) {
      const entries: { displayName: string; resultData: string[] }[] = [];
      for (const [, resultData] of dateMap.entries()) {
        entries.push({
          displayName: String(resultData.displayName),
          resultData: Array.isArray(resultData.resultData) ? resultData.resultData : []
        });
      }

      resultArray.push({
        date,
        entries
      });
    }

    resultArray.sort((a, b) => {
      const [dayA, monthA, yearA] = a.date.split('/').map(Number);
      const [dayB, monthB, yearB] = b.date.split('/').map(Number);

      const dateA = new Date(yearA, monthA - 1, dayA);
      const dateB = new Date(yearB, monthB - 1, dayB);

      return dateB.getTime() - dateA.getTime();
    })

    // Convert RTF to HTML for each resultData
    const htmlResults = await Promise.all(
      resultArray.map(async (dateEntry) => {
        return {
          date: dateEntry.date,
          entries: await Promise.all(
            dateEntry.entries.map(async (entry) => {
              return {
                displayName: entry.displayName,
                resultData: await Promise.all(entry.resultData.map(rtf => convertRtfToHtml(rtf)))
              };
            })
          )
        };
      })
    );

    return htmlResults;
  }

  private getImmunization(): void {
    this.loading = true;
    this.historicalDataService
      .getImmunizationByPatientId$(this.historicalData.patientId)
      .subscribe({
        next: (immunizations) => {
          immunizations.sort((a, b) => new Date(b.occurrenceDateTime).getTime() - new Date(a.occurrenceDateTime).getTime());

          this.setImmunization(immunizations);
          this.setImmunizationColumns();
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  private getConditionData(): void {
    this.loading = true;
    const { startDate, endDate } = this.historicalDataService.getNextRange();
    this.historicalDataService
      .getConditionsByPatientId$(this.historicalData.patientId, startDate, endDate)
      .subscribe({
        next: (conditions: ConditionResponse[]) => {
          this.setConditionRecords(conditions);
          this.setConditionColumns();
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  shouldApplyTitleCase(columnName: string): boolean {
    return this.historicalData.category === "immunization" && columnName === 'status';
  }

  handleMedicationRequestPageEvent(pageEvent: PageEvent) {
    this.medicationRequestPageIndex = pageEvent.pageIndex;
    this.medicationRequestPageSize = pageEvent.pageSize;
    this.setMedicalPageEntries();
    this.updateViewMoreVisibility<MedicalRequestEntry>(
      this.medicalRequestData,
      this.medicationRequestPageSize,
      this.medicationRequestPageIndex
    );
  }

  setMedicalPageEntries() {
    const startIndex =
      this.medicationRequestPageIndex * this.medicationRequestPageSize;
    const endIndex = startIndex + this.medicationRequestPageSize;

    this.medicalRequestCurPage = this.medicalRequestData.slice(
      startIndex,
      endIndex
    );
  }

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  setPagination(arr: any) {
    const total = arr?.length ?? 0;
    this.pageSize = total > 5 ? 5 : total;
    this.showPagination = total > this.pageSize;

  }

  reset() {
    this.medicalRequestData = [];
    this.medicalRequestCurPage = [];

    this.consultationHistory = [];
    this.consultationHistoryCurPage = [];

    this.tableData = [];
    this.showPagination = false;
    this.pageSize = 5;

    this.medicationRequestPageIndex = 0
    this.consultationHistoryPageIndex = 0;

    this.hasMoreData = true;
    this.showViewMoreBtn = false;
    this.expandedConsultationNoteKeys.clear();
    this.selectedLabEntryByDate = {};
    this.labTestSearchTerm = '';
    this.historicalDataService.resetDateRange();
  }
}
